import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  ExternalLink,
  Globe,
  Loader2,
  ShoppingCart,
  Unlock,
  KeyRound,
  Clock,
  ShieldOff,
} from 'lucide-react';
import {
  UniversalPage,
  Card,
  Button,
  Input,
  FormField,
  NotificationCard,
  useToast,
} from '@sighthost/ui';
import {
  domainService,
  TldNotSupportedError,
  TransferConfirmError,
} from '@/domains/domains-registry/services/domainService';
import {
  RegistrantForm,
  EMPTY_REGISTRANT,
  trimRegistrant,
  validateRegistrant,
  type RegistrantInput,
} from './components/RegistrantForm';

/**
 * G3 customer transfer-in wizard. Replaces the placeholder card.
 *
 * Three steps with progressive disclosure on a single page:
 *   1. Domain input + format + availability + customer-already-owns guard.
 *   2. Pre-flight checklist (thick per-registrar copy per locked G3-Q1).
 *   3. EPP / auth code + registrant form + submit → invoice.
 *
 * State persists to sessionStorage (FR-13) so customers don't lose data
 * while they hop to their losing registrar to grab the EPP code.
 *
 * Back-end is already shipped: `domain-transfer-confirm` EF + post-payment
 * saga + provisioning-runner v10's `runDomainTransfer` handler. This page
 * is purely client-side wiring.
 *
 * See `_bmad-output/PHASE_8E_GAP_G3_CUSTOMER_TRANSFER_IN_BRD.md` and
 * `_bmad-output/PHASE_8E_LOCKED_DECISIONS_2026-05-04.md` §G3.
 */

type WizardStep = 1 | 2 | 3;
const STORAGE_KEY = 'g3-transfer-wizard-v1';

interface PersistedState {
  step: WizardStep;
  domain: string;
  eppKey: string;
  registrant: RegistrantInput;
}

const FQDN_RE = /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;

function loadPersisted(): PersistedState | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedState>;
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      step: (parsed.step === 2 || parsed.step === 3 ? parsed.step : 1) as WizardStep,
      domain: typeof parsed.domain === 'string' ? parsed.domain : '',
      eppKey: typeof parsed.eppKey === 'string' ? parsed.eppKey : '',
      registrant: parsed.registrant ?? EMPTY_REGISTRANT,
    };
  } catch {
    return null;
  }
}

function persist(s: PersistedState) {
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch { /* quota */ }
}

function clearPersisted() {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* */ }
}

export default function TransferDomainPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const stored = loadPersisted();

  const [step, setStep] = useState<WizardStep>(stored?.step ?? 1);
  const [domain, setDomain] = useState<string>(stored?.domain ?? '');
  const [eppKey, setEppKey] = useState<string>(stored?.eppKey ?? '');
  const [registrant, setRegistrant] = useState<RegistrantInput>(stored?.registrant ?? EMPTY_REGISTRANT);

  // Persist on every change so a refresh during EPP-fetching doesn't lose state.
  useEffect(() => {
    persist({ step, domain, eppKey, registrant });
  }, [step, domain, eppKey, registrant]);

  // Step-1 transient state
  const [step1Checking, setStep1Checking] = useState(false);
  const [step1Error, setStep1Error] = useState<string | null>(null);
  const [step1WaitlistTld, setStep1WaitlistTld] = useState<string | null>(null);
  const [step1WaitlistEmail, setStep1WaitlistEmail] = useState('');
  const [step1WaitlistAdded, setStep1WaitlistAdded] = useState(false);
  const [step1WaitlistSubmitting, setStep1WaitlistSubmitting] = useState(false);

  // Step-3 transient state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  function startOver() {
    clearPersisted();
    setStep(1);
    setDomain('');
    setEppKey('');
    setRegistrant(EMPTY_REGISTRANT);
    setStep1Error(null);
    setStep1WaitlistTld(null);
    setStep1WaitlistEmail('');
    setStep1WaitlistAdded(false);
    setSubmitError(null);
  }

  async function onStep1Submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStep1Error(null);
    setStep1WaitlistTld(null);

    const fqdn = domain.trim().toLowerCase();
    if (!fqdn) {
      setStep1Error('Enter a domain name (e.g. mybusiness.co.za).');
      return;
    }
    if (!FQDN_RE.test(fqdn)) {
      setStep1Error('That doesn\'t look like a valid domain name. Use the form mybusiness.co.za.');
      return;
    }

    setStep1Checking(true);
    try {
      // Guard 1: do we already own this in our portal?
      const owned = await domainService.list();
      if (owned.some((d) => d.name.toLowerCase() === fqdn)) {
        setStep1Error(`${fqdn} is already in your SightHost portal — no transfer needed.`);
        return;
      }

      // Guard 2: registry-side availability. For transfer we want NOT available
      // (i.e. the domain is registered elsewhere and ready to be moved).
      const avail = await domainService.checkAvailability(fqdn);
      if (avail.available) {
        setStep1Error(
          `${fqdn} is currently available for registration — there's nothing at the registrar to transfer. ` +
            'If you want to register it new, use the Register flow instead.'
        );
        return;
      }

      // Both guards passed — domain is registered elsewhere AND not in this portal.
      setDomain(fqdn);
      setStep(2);
    } catch (err) {
      if (err instanceof TldNotSupportedError) {
        // Surface the waitlist CTA per locked G3-Q4.
        setStep1WaitlistTld(err.requested || fqdn.split('.').slice(-1)[0] || '');
      } else {
        setStep1Error(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setStep1Checking(false);
    }
  }

  async function onWaitlistSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (step1WaitlistSubmitting) return;
    if (!step1WaitlistEmail.trim()) return;
    setStep1WaitlistSubmitting(true);
    try {
      await domainService.addToGtldWaitlist(step1WaitlistEmail.trim(), domain.trim().toLowerCase());
      setStep1WaitlistAdded(true);
    } catch (err) {
      setStep1Error(err instanceof Error ? err.message : String(err));
    } finally {
      setStep1WaitlistSubmitting(false);
    }
  }

  function onStep2Continue() {
    setStep(3);
  }

  async function onStep3Submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitError(null);

    const validationError = validateRegistrant(registrant);
    if (validationError) {
      setSubmitError(validationError);
      return;
    }

    setSubmitting(true);
    try {
      const r = await domainService.transferConfirm({
        domain: domain.trim().toLowerCase(),
        epp_key: eppKey.trim() || undefined,
        registrant: trimRegistrant(registrant),
      });
      toast({
        tone: 'success',
        title: 'Invoice created',
        body: `Invoice ${r.invoice_number} for R${r.total_zar.toFixed(2)} — pay to start the transfer.`,
      });
      clearPersisted();
      navigate(`/app/billing/invoices/${r.invoice_id}`);
    } catch (err) {
      if (err instanceof TransferConfirmError) {
        setSubmitError(err.message);
        // tld_not_supported here would mean the allow-list changed since
        // step 1 — bounce back to start with a clear message.
        if (err.code === 'tld_not_supported') {
          setStep(1);
          setStep1Error(err.message);
        }
      } else {
        setSubmitError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <UniversalPage
      title="Transfer a domain"
      description="Bring an existing domain to SightHost with an EPP / auth code."
      breadcrumbs={[
        { label: 'Home', href: '/app' },
        { label: 'Domains', href: '/app/domains' },
        { label: 'Transfer' },
      ]}
    >
      <Stepper current={step} />

      {step === 1 && (
        <Step1
          domain={domain}
          setDomain={setDomain}
          checking={step1Checking}
          error={step1Error}
          waitlistTld={step1WaitlistTld}
          waitlistEmail={step1WaitlistEmail}
          setWaitlistEmail={setStep1WaitlistEmail}
          waitlistAdded={step1WaitlistAdded}
          waitlistSubmitting={step1WaitlistSubmitting}
          onSubmit={onStep1Submit}
          onWaitlistSubmit={onWaitlistSubmit}
        />
      )}

      {step === 2 && (
        <Step2 domain={domain} onBack={() => setStep(1)} onContinue={onStep2Continue} />
      )}

      {step === 3 && (
        <Step3
          domain={domain}
          eppKey={eppKey}
          setEppKey={setEppKey}
          registrant={registrant}
          setRegistrant={setRegistrant}
          submitting={submitting}
          error={submitError}
          onBack={() => setStep(2)}
          onSubmit={onStep3Submit}
          onStartOver={startOver}
        />
      )}
    </UniversalPage>
  );
}

// ─── Stepper ──────────────────────────────────────────────────────────

function Stepper({ current }: { current: WizardStep }) {
  const labels = ['Domain', 'Pre-flight', 'Registrant'];
  return (
    <ol className="mb-6 flex items-center gap-3 text-xs">
      {labels.map((label, idx) => {
        const n = (idx + 1) as WizardStep;
        const isCurrent = n === current;
        const isDone = n < current;
        return (
          <li key={label} className="flex items-center gap-3" aria-current={isCurrent ? 'step' : undefined}>
            <span
              className={[
                'flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold tabular-nums',
                isDone && 'bg-success-dark text-background',
                isCurrent && 'bg-warning-dark text-background',
                !isCurrent && !isDone && 'bg-muted text-muted-foreground',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {isDone ? <CheckCircle2 className="h-4 w-4" aria-hidden /> : n}
            </span>
            <span
              className={[
                'font-medium',
                isCurrent ? 'text-ink' : 'text-muted-foreground',
              ].join(' ')}
            >
              {label}
            </span>
            {n < 3 && <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />}
          </li>
        );
      })}
    </ol>
  );
}

// ─── Step 1 — domain input ────────────────────────────────────────────

interface Step1Props {
  domain: string;
  setDomain: (s: string) => void;
  checking: boolean;
  error: string | null;
  waitlistTld: string | null;
  waitlistEmail: string;
  setWaitlistEmail: (s: string) => void;
  waitlistAdded: boolean;
  waitlistSubmitting: boolean;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  onWaitlistSubmit: (e: FormEvent<HTMLFormElement>) => void;
}

function Step1(p: Step1Props) {
  return (
    <Card interactive={false}>
      <header className="border-b border-border px-6 py-4">
        <h3 className="text-sm font-semibold text-ink inline-flex items-center gap-2">
          <Globe className="size-4" aria-hidden />
          Which domain are you transferring?
        </h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Enter the domain that's currently registered with another registrar.
        </p>
      </header>

      <form onSubmit={p.onSubmit} className="space-y-4 p-6">
        <FormField label="Domain name" htmlFor="t-domain">
          <Input
            id="t-domain"
            value={p.domain}
            onChange={(e) => p.setDomain((e.target as HTMLInputElement).value)}
            placeholder="mybusiness.co.za"
            disabled={p.checking}
            autoFocus
            required
          />
        </FormField>

        {p.error && (
          <div
            role="alert"
            className="flex items-start gap-3 rounded-md border border-danger/30 bg-danger/5 p-3 text-sm text-danger"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{p.error}</span>
          </div>
        )}

        {p.waitlistTld && (
          <NotificationCard
            tone="info"
            title={`We don't yet handle .${p.waitlistTld}`}
            body={
              <div className="space-y-3">
                <p>
                  We're working with the registrar to add support. Drop your
                  email below and we'll let you know the moment it's live.
                </p>
                {p.waitlistAdded ? (
                  <p className="inline-flex items-center gap-2 text-success">
                    <CheckCircle2 className="size-4" aria-hidden /> You're on the list — thanks.
                  </p>
                ) : (
                  <form onSubmit={p.onWaitlistSubmit} className="flex flex-col gap-2 sm:flex-row sm:items-end">
                    <FormField label="Notify me when ready" htmlFor="t-waitlist-email">
                      <Input
                        id="t-waitlist-email"
                        type="email"
                        value={p.waitlistEmail}
                        onChange={(e) => p.setWaitlistEmail((e.target as HTMLInputElement).value)}
                        placeholder="you@example.com"
                        required
                      />
                    </FormField>
                    <Button type="submit" size="sm" loading={p.waitlistSubmitting} disabled={p.waitlistSubmitting}>Add me</Button>
                  </form>
                )}
              </div>
            }
          />
        )}

        <div className="flex justify-end pt-2">
          <Button type="submit" loading={p.checking} disabled={p.checking}>
            {p.checking ? <Loader2 className="size-4 animate-spin" /> : <ArrowRight className="size-4" />}
            {p.checking ? 'Checking…' : 'Continue'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

// ─── Step 2 — pre-flight checklist (thick per G3-Q1) ──────────────────

interface Step2Props {
  domain: string;
  onBack: () => void;
  onContinue: () => void;
}

const REGISTRAR_HINTS: Array<{ name: string; href: string }> = [
  { name: 'GoDaddy', href: 'https://www.godaddy.com/help/transfer-a-domain-to-another-registrar-7570' },
  { name: 'Afrihost', href: 'https://help.afrihost.com/transfer-a-domain-away/' },
  { name: '1-grid', href: 'https://www.1-grid.com/support/' },
  { name: 'Truehost', href: 'https://truehost.co.za/cloud/knowledgebase/' },
];

function Step2(p: Step2Props) {
  const [acknowledged, setAcknowledged] = useState(false);

  return (
    <Card interactive={false}>
      <header className="border-b border-border px-6 py-4">
        <h3 className="text-sm font-semibold text-ink">
          Before you continue
        </h3>
        <p className="mt-1 text-xs text-muted-foreground">
          A few things to check at your current registrar before we kick off
          the transfer of <strong className="text-ink">{p.domain}</strong>.
        </p>
      </header>

      <ol className="space-y-4 px-6 py-5">
        <PreFlightItem
          icon={Unlock}
          title="Unlock the domain at your current registrar"
          body="Most registrars enable a 'transfer lock' by default. You'll need to switch this off in their control panel before the transfer can proceed."
        />
        <PreFlightItem
          icon={KeyRound}
          title="Get the EPP / auth code"
          body="The current registrar will give you a one-time auth code (sometimes called an EPP key, transfer code, or auth-info code). You'll paste it on the next step."
        />
        <PreFlightItem
          icon={Clock}
          title="Domain must be more than 60 days old"
          body="ZACR and ICANN both block transfers within 60 days of registration or last transfer. If the domain is brand-new, wait it out."
        />
        <PreFlightItem
          icon={Clock}
          title="Domain expiry should be more than 30 days away"
          body="Registries refuse transfers when the domain is too close to expiry. If you're under 30 days, renew at the current registrar first, then transfer."
        />
        <PreFlightItem
          icon={ShieldOff}
          title="Disable WHOIS privacy if it's on"
          body="Some registries verify the registrant email during transfer; that won't work if the address is hidden behind privacy."
        />
      </ol>

      <div className="border-t border-border bg-card-subtle px-6 py-4">
        <p className="mb-2 text-xs font-semibold text-ink">
          Help articles for popular SA registrars
        </p>
        <ul className="flex flex-wrap gap-3 text-xs">
          {REGISTRAR_HINTS.map((r) => (
            <li key={r.name}>
              <a
                href={r.href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-warning-dark hover:underline"
              >
                {r.name}
                <ExternalLink className="size-3" aria-hidden />
              </a>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex items-center justify-between border-t border-border px-6 py-4">
        <label className="flex items-center gap-2 text-sm text-foreground-soft">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            className="size-4 rounded border-input text-warning-dark focus:ring-warning"
          />
          I've done these steps at the current registrar.
        </label>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={p.onBack}>Back</Button>
          <Button type="button" onClick={p.onContinue} disabled={!acknowledged}>
            <ArrowRight className="size-4" />
            Continue
          </Button>
        </div>
      </div>
    </Card>
  );
}

function PreFlightItem({
  icon: Icon,
  title,
  body,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  body: string;
}) {
  return (
    <li className="flex gap-3">
      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-warning/10 text-warning-dark">
        <Icon className="size-4" />
      </span>
      <div>
        <p className="text-sm font-semibold text-ink">{title}</p>
        <p className="text-sm text-foreground-soft">{body}</p>
      </div>
    </li>
  );
}

// ─── Step 3 — EPP + Registrant + Submit ───────────────────────────────

interface Step3Props {
  domain: string;
  eppKey: string;
  setEppKey: (s: string) => void;
  registrant: RegistrantInput;
  setRegistrant: (r: RegistrantInput) => void;
  submitting: boolean;
  error: string | null;
  onBack: () => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  onStartOver: () => void;
}

function Step3(p: Step3Props) {
  return (
    <Card interactive={false}>
      <header className="border-b border-border px-6 py-4">
        <h3 className="text-sm font-semibold text-ink">
          Your details for {p.domain}
        </h3>
        <p className="mt-1 text-xs text-muted-foreground">
          ZACR requires a registrant on every <code>.za</code> transfer.
          This is the contact the registry will write into the WHOIS for{' '}
          <strong className="text-ink">{p.domain}</strong>.
        </p>
      </header>

      <form onSubmit={p.onSubmit} className="space-y-6 p-6">
        <FormField
          label="EPP / auth code"
          htmlFor="t-epp"
          helper="Optional for .co.za but recommended. Required for gTLDs like .com (when supported)."
        >
          <Input
            id="t-epp"
            value={p.eppKey}
            onChange={(e) => p.setEppKey((e.target as HTMLInputElement).value)}
            placeholder="e.g. AB12-CD34-EF56"
            disabled={p.submitting}
          />
        </FormField>

        <RegistrantForm
          value={p.registrant}
          onChange={p.setRegistrant}
          disabled={p.submitting}
          idPrefix="t-reg"
        />

        {p.error && (
          <div
            role="alert"
            className="flex items-start gap-3 rounded-md border border-danger/30 bg-danger/5 p-3 text-sm text-danger"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{p.error}</span>
          </div>
        )}

        <div className="flex items-center justify-between border-t border-border pt-4">
          <button
            type="button"
            onClick={p.onStartOver}
            className="text-xs text-muted-foreground hover:text-warning-dark hover:underline"
          >
            Start over
          </button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={p.onBack} disabled={p.submitting}>
              Back
            </Button>
            <Button type="submit" loading={p.submitting} disabled={p.submitting}>
              {p.submitting ? null : <ShoppingCart className="size-4" />}
              {p.submitting ? 'Creating invoice…' : 'Continue to payment'}
            </Button>
          </div>
        </div>
      </form>
    </Card>
  );
}
